/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, ReactNode } from "react"
import { Drawer } from "@duxton-web/ui"
import { ChevronRightUniversal } from "@duxton-web/icons"

import styles from "./MoreActionsSheet.modules.css"

export interface MoreActionsSheetAction {
  key: string
  icon: ReactNode
  label: string
  /**
   * The row's destination, rendered as a REAL anchor: WKWebView reports an anchor
   * click to the app as `.linkActivated` — what its URL interception keys on —
   * while scripted `location.href` arrives as `.other` and navigates nowhere.
   * Same rule as the timeline's action links.
   */
  href: string
}

interface MoreActionsSheetProps {
  open: boolean
  /**
   * Names the dialog for assistive tech — the sheet has no visible chrome of its own
   * (`closable` is false) and, unlike `OptionPickerSheet`, no heading either.
   */
  title: string
  actions: MoreActionsSheetAction[]
  onCancel(): void
}

/**
 * Bottom-sheet action menu for the webview, where the design replaces the desktop
 * header dropdown with sheet rows (leading icon, label, trailing chevron). Each row
 * is a plain anchor to its destination; tapping one also dismisses the sheet, so the
 * navigation never happens under an open sheet.
 */
export const MoreActionsSheet: FC<MoreActionsSheetProps> = ({
  open,
  title,
  actions,
  onCancel,
}) => (
  <Drawer
    open={open}
    onClose={onCancel}
    placement="bottom"
    height="auto"
    closable={false}
    className={styles.sheet}
    /*
     * `body` zeroes Duxton's own `paddingLG`, which would otherwise stack with
     * `.content`'s inset — the sibling picker sheets zero it for the same reason.
     *
     * `wrapper` is a STYLE, not a className: Duxton rebuilds rc-drawer's class map
     * with only `mask` and `content`, so a `classNames.wrapper` never reaches the
     * element. It carries the drop shadow, which would render square behind the
     * rounded content.
     */
    styles={{
      body: { padding: 0 },
      wrapper: { borderRadius: "16px 16px 0 0" },
    }}
    aria-label={title}
  >
    <div className={styles.content}>
      {/*
       * No `target` on the rows — a scheme destination must stay in this document,
       * or the browser strands an empty tab on the deeplink URL. The tap also
       * dismisses the sheet, alongside the navigation the anchor performs itself.
       */}
      {actions.map(action => (
        <a
          key={action.key}
          className={styles.row}
          href={action.href}
          onClick={onCancel}
        >
          <span className={styles.rowIcon} aria-hidden>
            {action.icon}
          </span>
          <span className={styles.rowLabel}>{action.label}</span>
          <span className={styles.rowChevron} aria-hidden>
            <ChevronRightUniversal />
          </span>
        </a>
      ))}
    </div>
  </Drawer>
)
